'use client';

import { EssenceResponse } from '@repo/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { services } from '@repo/ui/lib/api/services';

export interface EssenceImageUploadParams {
  epaysageId: number;
  essenceId: number;
  field: 'img1' | 'img2' | 'img3';
  file: File;
}

export interface EssenceImageDeleteParams {
  epaysageId: number;
  essenceId: number;
  field: 'img1' | 'img2' | 'img3';
}

export interface EssenceImageRotateParams {
  epaysageId: number;
  essenceId: number;
  field: 'img1' | 'img2' | 'img3';
  rotation: number;
}

export function useEssenceImageOperations() {
  const queryClient = useQueryClient();

  const invalidate = (epaysageId: number, essenceId: number) => {
    queryClient.invalidateQueries({ queryKey: ['essences', epaysageId] });
    queryClient.invalidateQueries({ queryKey: ['essence', epaysageId, essenceId] });
  };

  const upload = useMutation({
    mutationFn: async ({ epaysageId, essenceId, field, file }: EssenceImageUploadParams): Promise<EssenceResponse> => {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Type de fichier non supporté. Utilisez JPEG, PNG ou WebP.');
      }

      const maxSize = 20 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('Le fichier est trop volumineux. Taille maximale: 5MB.');
      }

      return services.essence.updateImage(epaysageId, essenceId, field, file);
    },
    onSuccess: (_, { epaysageId, essenceId }) => invalidate(epaysageId, essenceId),
  });

  const remove = useMutation({
    mutationFn: ({ epaysageId, essenceId, field }: EssenceImageDeleteParams): Promise<EssenceResponse> => {
      return services.essence.deleteImage(epaysageId, essenceId, field);
    },
    onSuccess: (_, { epaysageId, essenceId }) => invalidate(epaysageId, essenceId),
  });

  const rotate = useMutation({
    mutationFn: ({ epaysageId, essenceId, field, rotation }: EssenceImageRotateParams): Promise<EssenceResponse> => {
      if (![0, 90, 180, 270].includes(rotation)) {
        throw new Error('La rotation doit être 0, 90, 180 ou 270 degrés');
      }
      return services.essence.rotateImage(epaysageId, essenceId, field, rotation);
    },
    onSuccess: (_, { epaysageId, essenceId }) => invalidate(epaysageId, essenceId),
  });

  return {
    upload,
    delete: remove,
    rotate,
    isLoading: upload.isPending || remove.isPending || rotate.isPending,
    error: upload.error || remove.error || rotate.error,
  };
}

